import { clampPanToWorld, clampTimelineZoom } from "../WorldMetrics/metrics";

export interface TimelineViewTransform {
  zoom: number;
  panX: number;
  panY: number;
}

const PAN_DRAG_SKIP = ".timeline-sheet, .timeline-transport, .timeline-options, .timeline-scene-overlay__summary, button, a, input";

/**
 * Sideways two-finger swipe. Wheel deltaX points opposite the fingers, so
 * subtracting it moves the timeline with the swipe. Vertical movement stays
 * on the zoom gesture.
 */
export function applyWheelPanToViewTransform(
  view: TimelineViewTransform,
  stageW: number,
  stageH: number,
  deltaX: number,
): TimelineViewTransform {
  const pan = clampPanToWorld(
    view.panX - deltaX,
    view.panY,
    view.zoom,
    stageW,
    stageH,
  );
  return { zoom: view.zoom, ...pan };
}

export function applyWheelToViewTransform(
  view: TimelineViewTransform,
  stageW: number,
  stageH: number,
  pointerX: number,
  pointerY: number,
  factor: number,
): TimelineViewTransform {
  const worldX = (pointerX - view.panX) / view.zoom;
  const worldY = (pointerY - view.panY) / view.zoom;
  const nextZoom = clampTimelineZoom(view.zoom * factor);
  const panX = pointerX - worldX * nextZoom;
  const panY = pointerY - worldY * nextZoom;
  const pan = clampPanToWorld(panX, panY, nextZoom, stageW, stageH);
  return { zoom: nextZoom, ...pan };
}

export function applyDragToViewTransform(
  startView: TimelineViewTransform,
  startPointerX: number,
  startPointerY: number,
  pointerX: number,
  pointerY: number,
  stageW: number,
  stageH: number,
): TimelineViewTransform {
  const panX = startView.panX + (pointerX - startPointerX);
  const panY = startView.panY + (pointerY - startPointerY);
  const pan = clampPanToWorld(
    panX,
    panY,
    startView.zoom,
    stageW,
    stageH,
  );
  return { zoom: startView.zoom, ...pan };
}

export function viewTransformCenteredOn(
  worldX: number,
  worldY: number,
  zoom: number,
  stageW: number,
  stageH: number,
): TimelineViewTransform {
  // Round the zoom first. Far along the rail, rounding it after placing the pan moves everyone.
  const steady = stabilizeViewTransform({ zoom, panX: 0, panY: 0 }).zoom;
  const panX = stageW / 2 - worldX * steady;
  const panY = stageH / 2 - worldY * steady;
  const pan = clampPanToWorld(panX, panY, steady, stageW, stageH);
  return stabilizeViewTransform({ zoom: steady, ...pan });
}

/** Reduce subpixel pan jitter (grid / CSS flicker while camera follows). */
export function stabilizeViewTransform(
  view: TimelineViewTransform,
): TimelineViewTransform {
  return {
    zoom: Math.round(view.zoom * 10_000) / 10_000,
    panX: Math.round(view.panX * 2) / 2,
    panY: Math.round(view.panY * 2) / 2,
  };
}

export function canStartTimelinePanDrag(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return target.closest(PAN_DRAG_SKIP) === null;
}

